/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SampleConstants from 'constants/SampleConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {requestFetchManagedModel} from 'models/ManagedModelUtil';
import StorableStore from 'samples/StorableStore';
import APIUtil from 'util/APIUtil';

function fetchContainable(containableId: any): void {
  if (!containableId) {
    return;
  }

  if (APIUtil.isBiologicalEntity(containableId)) {
    requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, containableId);
  } else {
    assert(APIUtil.apiIdToType(containableId) === ModelTypeConstants.BATCH, 'Expected batch');
    requestFetchManagedModel(ManagedModelType.BATCH, containableId);
  }
}

interface RequiredProps {
  batch: any;
  bioentity: any;
}

/**
 * Wrapper component to render a component with a given containable loaded. It gets containable from
 * reduxStore if bioentity or StorableStore if batch.
 *
 * EXAMPLE: Say ContainableComponent externally takes inputContainableId prop and internally takes
 * inputContainable prop. Wrap it this way:
 *   withContainableLoading(
 *     ContainableComponent,                            // WrappedComponent
 *     (props) => props.inputContainableId,             // propsToContainableId
 *     (containable) => {inputContainable: containable} // containableToProps
 *   )
 */
export default function withContainableLoading<P extends RequiredProps>(
  WrappedComponent: any,
  propsToContainableId: any,
  containableToProps: any
): typeof WrappedComponent {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function mapStateToProps(state: any, props: any) {
    const containableId = propsToContainableId(props);
    const isBioentity = containableId && APIUtil.isBiologicalEntity(containableId);
    return {
      bioentity: isBioentity ? state.models.fileSummariesById[containableId] : null,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function mapStorableStoreToProps(storableStore: any, props: any) {
    const containableId = propsToContainableId(props);
    const isBatch = containableId && APIUtil.apiIdToType(containableId) === ModelTypeConstants.BATCH;
    return {
      batch: isBatch ? storableStore.getBatch(containableId) : null,
    };
  }

  return compose(
    connect(mapStateToProps),
    StorableStore.connect(mapStorableStoreToProps)
  )(
    class extends React.Component<P> {
      static propTypes = {
        // Props provided by connect
        batch: SampleConstants.CONTAINABLE_SUMMARY_SHAPE,
        bioentity: SampleConstants.CONTAINABLE_SUMMARY_SHAPE,
      };

      componentDidMount(): void {
        fetchContainable(propsToContainableId(this.props));
      }

      componentDidUpdate(): void {
        fetchContainable(propsToContainableId(this.props));
      }

      render(): JSX.Element {
        const containable = this.props.batch || this.props.bioentity;
        return (
          <WrappedComponent
            // eslint-disable-next-line no-restricted-syntax
            {..._.omit(this.props, 'batch', 'bioentity')}
            {...containableToProps(containable)}
          />
        );
      }
    }
  );
}
